// mastery.ts — P1.3 topic-mastery + "facts learned" collection (Trivia Munchers 2.0).
//
// The product reset wants progression that rewards KNOWLEDGE, not just score: a per-topic mastery map
// and a growing collection of facts the player has actually learned (from the result explanation
// cards). This is PRESENTATION/meta only — a thin, best-effort localStorage wrapper exactly like
// stats.ts; it never touches Core/Sim/determinism and reads Date only on the renderer side.
//
// It deliberately keeps its OWN storage key (separate from stats' 'trivia.stats.v1') so it composes
// with the existing retention layer without forking it: mastery LEVEL is derived from cumulative
// correct CHOMPs per topic recorded here, and the facts collection is appended when a result
// explanation is shown. Reads are pure; writes are dedup'd and capped.

const KEY = 'trivia.mastery.v1';
const MAX_FACTS = 500;

/** One fact the player learned, captured from a result explanation card. */
export interface LearnedFact {
  /** normalized topic slug the fact belongs to */
  topic: string;
  /** the answer/term the fact is about (e.g. "Pluto") */
  label: string;
  /** the short explanation ("It is classified as a dwarf planet.") */
  fact: string;
  /** UTC day key (YYYY-MM-DD) the fact was first learned — drives the "facts today" daily goal */
  day: string;
}

export interface MasteryState {
  /** cumulative correct CHOMPs per normalized topic slug — the mastery-level signal */
  topicCorrect: Record<string, number>;
  /** the learned-facts collection (most-recent last), dedup'd by topic+label, capped at MAX_FACTS */
  facts: LearnedFact[];
}

/** A topic's mastery tier (0..5) plus a human label, derived from cumulative correct answers. */
export interface TopicMastery {
  topic: string;
  correct: number;
  level: number;
  label: string;
}

// Mastery tiers by cumulative correct CHOMPs in a topic. Index = level; `at` = entry threshold.
const TIERS: { name: string; at: number }[] = [
  { name: 'NEW', at: 0 },
  { name: 'LEARNING', at: 5 },
  { name: 'SKILLED', at: 15 },
  { name: 'EXPERT', at: 30 },
  { name: 'MASTER', at: 60 },
  { name: 'LEGEND', at: 120 },
];

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function utcDay(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaults(): MasteryState {
  return { topicCorrect: {}, facts: [] };
}

/** Read the mastery blob, tolerating private-mode / blocked / corrupt storage (best-effort). */
export function getMastery(): MasteryState {
  const store = storage();
  const raw = store?.getItem(KEY);
  if (!raw) return defaults();
  try {
    const parsed = JSON.parse(raw) as Partial<MasteryState>;
    const topicCorrect: Record<string, number> = {};
    if (parsed.topicCorrect && typeof parsed.topicCorrect === 'object') {
      for (const [k, v] of Object.entries(parsed.topicCorrect)) {
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) topicCorrect[k] = Math.floor(v);
      }
    }
    const facts = Array.isArray(parsed.facts)
      ? parsed.facts
          .filter(
            (f): f is LearnedFact =>
              !!f && typeof f.topic === 'string' && typeof f.label === 'string' && typeof f.fact === 'string',
          )
          .slice(-MAX_FACTS)
          .map((f) => ({ topic: f.topic, label: f.label, fact: f.fact, day: typeof f.day === 'string' ? f.day : '' }))
      : [];
    return { topicCorrect, facts };
  } catch {
    return defaults();
  }
}

function persist(s: MasteryState): void {
  try {
    storage()?.setItem(KEY, JSON.stringify(s));
  } catch {
    // Storage can be blocked (private mode); mastery is a best-effort enhancement, never load-bearing.
  }
}

/** Add `n` correct CHOMPs to a topic's cumulative mastery signal. Returns the new TopicMastery. */
export function recordTopicCorrect(topic: string, n: number): TopicMastery {
  const s = getMastery();
  const add = Math.max(0, Math.floor(n));
  s.topicCorrect[topic] = (s.topicCorrect[topic] ?? 0) + add;
  persist(s);
  return topicMastery(topic, s);
}

/**
 * Record a fact the player just learned (from a result explanation). Dedup'd by topic+label so the
 * same answer never clutters the collection twice; returns true only when a NEW fact was added (so
 * the caller can celebrate "New fact learned!"). Best-effort.
 */
export function recordFactLearned(topic: string, label: string, fact: string): boolean {
  if (!topic || !label || !fact) return false;
  const s = getMastery();
  const exists = s.facts.some((f) => f.topic === topic && f.label === label);
  if (exists) return false;
  s.facts.push({ topic, label, fact, day: utcDay() });
  if (s.facts.length > MAX_FACTS) s.facts = s.facts.slice(-MAX_FACTS);
  persist(s);
  return true;
}

/** The mastery tier for a topic, from cumulative correct answers (reads storage unless `s` given). */
export function topicMastery(topic: string, s: MasteryState = getMastery()): TopicMastery {
  const correct = s.topicCorrect[topic] ?? 0;
  let level = 0;
  for (let i = TIERS.length - 1; i >= 0; i -= 1) {
    if (correct >= TIERS[i].at) {
      level = i;
      break;
    }
  }
  return { topic, correct, level, label: TIERS[level].name };
}

/** Total distinct facts the player has collected. */
export function factsLearnedTotal(s: MasteryState = getMastery()): number {
  return s.facts.length;
}

/** How many facts were learned on a given UTC day (defaults to today) — powers the daily knowledge goal. */
export function factsLearnedOn(day: string = utcDay(), s: MasteryState = getMastery()): number {
  return s.facts.reduce((n, f) => (f.day === day ? n + 1 : n), 0);
}

/** Topics ranked by mastery level then cumulative correct — for a mastery-map panel (most-mastered first). */
export function masteryMap(s: MasteryState = getMastery()): TopicMastery[] {
  return Object.keys(s.topicCorrect)
    .map((t) => topicMastery(t, s))
    .sort((a, b) => b.level - a.level || b.correct - a.correct);
}
